import { useGetMe } from "@elation/api-client/src/stitch-web";
import { Actor } from "@src/actor";
import { authClient } from "@src/auth/client";
import { BEARER_TOKEN_KEY } from "@src/auth/client.constants";
import { IS_IN_IFRAME } from "@src/env";
import { requestAuthTokenFromParent } from "@src/features/iframe/messages";
import { navigation } from "@src/navigation";
import { appStore } from "@src/stores/app";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Navigate } from "react-router-dom";

export const ActorContext = createContext<Actor | null>(null);

/**
 * Represents who is acting within the application right now.
 *
 * Resolves once on login and remains stable for the session.
 * Used to attribute actions to the correct user and role for auditing.
 */
export function ActorProvider({ children }: { children: ReactNode }) {
  const {
    data: session,
    isPending: isPendingSession,
    error: sessionError,
    refetch: refetchSession,
  } = authClient.useSession();

  const {
    data,
    error: getMeError,
    isLoading: isInitialLoadingMe,
  } = useGetMe({
    query: {
      enabled: session !== null,
      retry: false,
    },
  });

  const practiceTimezone = data?.data?.practice.timezone;

  useEffect(() => {
    if (!practiceTimezone) {
      return;
    }

    appStore.getState().setPracticeTimezone(practiceTimezone);
  }, [practiceTimezone]);

  // In iframe mode, the bearer token in localStorage can go stale mid-session
  // (e.g. token TTL elapses while the user is active). Better Auth then
  // returns no session, which would otherwise drop the user to /login with a
  // generic error. Ask the parent (the EHR host) for a fresh token and
  // refetch the session before deciding the session is actually dead.
  //
  // We bound the retry: if the parent can't produce a usable token after
  // MAX attempts, fall through to the redirect path so the user sees a real
  // error instead of an indefinitely blank panel. Hippo wires this up by
  // passing `onRefreshAuthToken` to <StitchIframe>, which lets the parent
  // actively mint a fresh token rather than echoing whatever stale value
  // its `authToken` prop happens to hold.
  const MAX_IFRAME_REFRESH_ATTEMPTS = 3;
  const [iframeRefreshState, setIframeRefreshState] = useState<
    "idle" | "in-flight" | "settled"
  >(IS_IN_IFRAME ? "idle" : "settled");
  const iframeRefreshAttemptsRef = useRef(0);
  // Tab refocus fires `focus` and `visibilitychange` back-to-back. React
  // state updates from the first handler are batched, so the second
  // handler's closure still sees `iframeRefreshState === "settled"` and
  // would fire a second concurrent attempt — burning two of three
  // retries on a single refocus. Track in-flight status with a ref so
  // the guard is synchronous.
  const iframeRefreshInFlightRef = useRef(false);

  const attemptIframeSessionRefresh = useCallback(async () => {
    if (iframeRefreshInFlightRef.current) return;
    iframeRefreshInFlightRef.current = true;
    iframeRefreshAttemptsRef.current += 1;
    setIframeRefreshState("in-flight");

    try {
      const { token } = await requestAuthTokenFromParent();
      if (!token) return;

      localStorage.setItem(BEARER_TOKEN_KEY, token);
      await refetchSession();
    } finally {
      iframeRefreshInFlightRef.current = false;
      setIframeRefreshState("settled");
    }
  }, [refetchSession]);

  useEffect(() => {
    if (!IS_IN_IFRAME) return;
    if (isPendingSession) return;

    if (session) {
      // Session is valid: arm the next refresh attempt for if/when it expires again.
      iframeRefreshAttemptsRef.current = 0;
      setIframeRefreshState("settled");
      return;
    }

    if (iframeRefreshAttemptsRef.current > 0) return;
    void attemptIframeSessionRefresh();
  }, [session, isPendingSession, attemptIframeSessionRefresh]);

  useEffect(() => {
    if (!IS_IN_IFRAME) return;

    const retryOnForeground = () => {
      if (document.visibilityState === "hidden") return;
      if (isPendingSession) return;
      if (session) return;
      if (iframeRefreshState === "in-flight") return;
      if (iframeRefreshAttemptsRef.current >= MAX_IFRAME_REFRESH_ATTEMPTS) {
        return;
      }

      void attemptIframeSessionRefresh();
    };

    window.addEventListener("focus", retryOnForeground);
    document.addEventListener("visibilitychange", retryOnForeground);

    return () => {
      window.removeEventListener("focus", retryOnForeground);
      document.removeEventListener("visibilitychange", retryOnForeground);
    };
  }, [
    session,
    isPendingSession,
    iframeRefreshState,
    attemptIframeSessionRefresh,
  ]);

  if (isPendingSession || isInitialLoadingMe) {
    return <></>; // Todo: maybe add loading UI here
  }

  // Hold the render while we still have refresh attempts left to spend. The
  // ref is the synchronous source of truth — on the render where session
  // transitions truthy → null, the effect that bumps the counter and flips
  // state to "in-flight" hasn't run yet, but `attemptsRef` already reflects
  // the prior cycle's reset to 0, so we know more attempts are coming.
  if (
    IS_IN_IFRAME &&
    !session &&
    (iframeRefreshAttemptsRef.current === 0 ||
      iframeRefreshState === "in-flight" ||
      (iframeRefreshAttemptsRef.current < MAX_IFRAME_REFRESH_ATTEMPTS &&
        !sessionError &&
        !getMeError))
  ) {
    return <></>;
  }

  if (!session || sessionError || getMeError || !data?.data) {
    const meErrorMessage = getMeError
      ? "Unable to retrieve user profile"
      : undefined;

    const sessionErrorMessage = sessionError?.message
      ? "Unable to verify your session. Please try logging in again."
      : undefined;

    // In iframe mode the only way to land here without a concrete error is
    // that the bounded parent-token retry exhausted itself — i.e., the host
    // EHR couldn't supply a usable token. Give the user a message that
    // matches what actually happened, so the Refresh button on the iframe
    // login page has obvious meaning.
    const iframeNoTokenMessage =
      IS_IN_IFRAME && !session && !sessionError && !getMeError
        ? "We couldn't refresh your session from the host application. Click Refresh to try again."
        : undefined;

    const error =
      sessionErrorMessage ??
      meErrorMessage ??
      iframeNoTokenMessage ??
      "An unexpected error occurred";

    console.error("[ActorProvider] Auth failed, redirecting to login", {
      hasSession: !!session,
      sessionError: sessionError?.message,
      getMeError: getMeError?.message,
      hasData: !!data?.data,
    });

    return (
      <Navigate to={navigation.login(new URLSearchParams({ error }))} replace />
    );
  }

  const me = data.data;

  const actor: Actor =
    me.type === "physician"
      ? {
          type: "physician",
          user: me.user,
          practice: me.practice,
          patientSyncJobId: me.patientSyncJobId,
          config: me.config,
        }
      : {
          type: "staff",
          user: me.user,
          practice: me.practice,
          config: me.config,
        };

  return (
    <ActorContext.Provider value={actor}>{children}</ActorContext.Provider>
  );
}

/**
 * Returns the current applicationactor. Must be used within an {@link ActorProvider}.
 */
export function useActor(): Actor {
  const actor = useContext(ActorContext);

  if (!actor) {
    throw new Error("useActor must be used within an ActorProvider");
  }

  return actor;
}

/**
 * Returns the current actor narrowed to the given type.
 *
 * Use this in components that are only reachable by a specific actor type.
 *
 * @throws If the current actor's type does not match the expected type.
 */
export function useAssertActor<T extends Actor["type"]>(
  type: T,
): Extract<Actor, { type: T }> {
  const actor = useActor();

  if (actor.type !== type) {
    throw new Error(`Expected actor type "${type}", got "${actor.type}"`);
  }

  return actor as Extract<Actor, { type: T }>;
}
